
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, inject } from "@angular/core";
import { Broadcaster } from "./broadcaster.service";
import { ConfigService } from "./config.service";

/**
 * The Android back button closes whatever is on top of the dialog stack. Each entry maps
 * the selector that proves a dialog is open to the broadcast that closes it, in priority
 * order - the first match wins, exactly as the original if/else chain did.
 *
 * This was 190 lines of hand-written else-if in AppComponent (STR-05). Adding a dialog
 * now means adding a row here rather than editing the shell.
 */
export const BACK_BUTTON_DIALOG_STACK: { selector: string; event: string }[] = [
  { selector: "vp-change-server-dialog", event: "hideChangeServerDialog" },
  { selector: ".registration-activation", event: "hideRegistrationActivationPage" },
  { selector: ".registration-form", event: "hideRegistrationFormPage" },
  { selector: ".forgotpassword-info", event: "hideForgotPasswordInfoPage" },
  { selector: ".forgotpassword-form", event: "hideForgotPasswordFormPage" },
  { selector: ".passwordreset-form", event: "hideResetPasswordFormPage" },
  { selector: ".vp-task-confirm-dialog", event: "hideTaskConfirmDialog" },
  { selector: ".vp-save-query-dialog", event: "hideSaveQueryDialog" },
  { selector: ".vp-description-diff-dialog", event: "hideDiscriptionDiffDialog" },
  { selector: ".vp-task-watchers-dialog", event: "hideWatcherDialog" },
  { selector: ".vp-task-recording-voice-dialog", event: "hideTaskRecordVoiceMsgDialog" },
  { selector: ".vp-task-attachment-option-dialog", event: "hideTaskAttachmentOptionDialog" },
  // The original chain tested .vp-task-add-attachment-dialog twice in a row; the second
  // branch was unreachable, so it is not repeated here.
  { selector: ".vp-task-add-attachment-dialog", event: "hideTaskAddAttachmentDialog" },
  { selector: ".vp-task-reminder-dialog", event: "hideTaskAddReminderDialog" },
  { selector: ".vp-task-add-fields-dialog", event: "hideTaskAddFieldDialog" },
  { selector: ".media-preview-dialog-mobile", event: "hideMediaPreviewDialog" },
  { selector: ".vp-log-time-dialog", event: "hideLogTimeDialog" },
  { selector: ".vp-timelog-history-dialog", event: "hideTimelogHistoryDialog" },
  { selector: ".vp-vnctask-details", event: "hideTaskDetails" },
  { selector: ".vnctask-bulk-edit-dialog", event: "hideBulkEditDialog" },
  { selector: "vp-tasks-dialog", event: "hideTasksDialog" },
  { selector: ".vnctask_reminer_dialog", event: "hideReminderDialog" },
  { selector: ".vnctask-compose-dialog", event: "hideTaskComposeDialog" },
  { selector: ".vp-archive-users-dialog", event: "hideArchiveUsersDialog" },
  { selector: ".vp-invite-users-dialog", event: "hideInviteUsersDialog" },
  { selector: ".vp-users-management-dialog", event: "hideUsersManagementDialog" },
  { selector: ".vp-task-legal-notice-dialog", event: "hideTaskLegalNoticeDialog" },
  { selector: ".vp-task-service-desk-dialog", event: "hideTaskServiceDeskDialog" },
  { selector: ".vp-task-help-faq-dialog", event: "hideTaskHelpFaqDialog" },
  { selector: ".vp-task-deactivate-confirmation-dialog", event: "hideTaskAccountDeactivateConfirmation" },
  { selector: ".vp-task-deactivate-feedback-dialog", event: "hideTaskAccountDeactivateFeedback" },
  { selector: "vp-tfa-settings", event: "hideTfaSettings" },
  { selector: ".vp-task-general-settings-dialog", event: "hideTaskGeneralSettingsDialog" },
  { selector: ".vp-task-changelog-dialog", event: "hideTaskChangelogDialog" },
  { selector: ".vp-task-aboutus-dialog", event: "hideTaskAboutusDialog" },
  { selector: ".vp-create-list-dialog", event: "hideCreateListDialogPage" },
  { selector: ".vp-folder-option-list-item-dialog", event: "hideFolderOptionListItemDialog" },
  { selector: ".vp-folder-option-list-dialog", event: "hideFolderOptionListDialog" },
  { selector: ".vp-create-location-dialog", event: "hideCreateLocationDialogPage" },
  { selector: ".vp-location-option-list-dialog", event: "hideLocationOptionListDialog" },
  { selector: ".vp-task-option-list-dialog", event: "hideTaskOptionListDialog" },
  { selector: ".vp-tag-option-list-dialog", event: "hideTagOptionListDialog" },
  { selector: ".vp-save-search-dialog", event: "hideSaveSearchDialog" },
  { selector: ".vp-apps-dialog", event: "hideAppsDialog" },
  { selector: ".vp-task-settings-dialog", event: "hideTaskSettingsDialog" },
  { selector: ".vp-task-image-cropper-dialog", event: "hideTaskImageCropperDialog" },
  { selector: ".vp-task-change-password-dialog", event: "hideTaskChangePasswordDialog" },
  { selector: ".vnc_task_login_profile_dialog", event: "hideTaskLoginProfileDialog" },
  { selector: "vp-select-apps-dialog", event: "hideTaskSelectAppsDialog" },
  { selector: "vp-duedate-range", event: "hideTaskDuedateDialog" },
  { selector: "vp-search-through", event: "hideTaskSearchThroughDialog" },
  { selector: "vp-select-projects", event: "hideTaskSelectProjectsDialog" },
  { selector: "vp-select-users", event: "hideTaskSelectUsersDialog" },
  { selector: "vp-select-tags", event: "hideTaskSelectTagsDialog" },
  { selector: "vp-select-lists", event: "hideTaskSelectListsDialog" },
  { selector: "vp-select-locations", event: "hideTaskSelectLocationsDialog" },
  { selector: "vp-time-range", event: "hideTaskTimeRangeDialog" },
  { selector: ".advance-search-dialog", event: "hideAdvanceTaskSearchDialog" },
  { selector: ".vp-mobile-search", event: "hideMobileSearchDialog" }
];

@Injectable({
  providedIn: "root"
})
export class BackButtonService {
  private broadcaster = inject(Broadcaster);
  private configService = inject(ConfigService);

  /**
   * Resolves one Android back-button press against the dialog stack.
   * The login/registration iframes are handled before the table because they are not
   * closed by a broadcast: they are removed from the DOM, or they exit the app.
   */
  public handleBackButton(): void {
    if (document.querySelector("#registrationIframe") !== null) {
      document.querySelector("#registrationIframe").remove();
      return;
    }

    if (document.querySelector("#forgotPasswordIframe") !== null) {
      document.querySelector("#forgotPasswordIframe").remove();
      return;
    }

    if (document.querySelector("#loginIframe") !== null) {
      navigator.app.exitApp();
      return;
    }

    if (document.querySelector("#tfaOtpIframe") !== null) {
      this.configService.hideTfaOtpIframe();
      this.configService.loginIframe();
      return;
    }

    const open = BACK_BUTTON_DIALOG_STACK.find(entry => document.querySelector(entry.selector) !== null);
    if (open) {
      this.broadcaster.broadcast(open.event);
      console.log("[BackButtonService] onBackButton", open.event);
    }
  }
}
